import React, { ForwardedRef, forwardRef } from "react";
import classNames from "classnames";
import { IconProps, InputProps, Size } from "./types";

const getIconProps = (size: Size): IconProps => {
  const iconSize = size === "md" || size === "lg" ? 16 : 14;
  return {
    fill: "currentColor",
    className: "rds-Input-icon",
    height: iconSize,
    width: iconSize,
    ["aria-hidden"]: true,
    focusable: false,
  };
};

/**
 *
 * Demos:
 *
 * - [RDS Input](https://design.ramseysolutions.com/storybook/?path=/story/design-system-input--default)
 *
 * API:
 *
 * - [RDS Input API](https://design.ramseysolutions.com/components/input/?tab=technologies#react)
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      disabled = false,
      icon: InputIcon,
      iconPosition = "left",
      invalid = false,
      length,
      size = "md",
      ...rest
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>
  ): React.JSX.Element => {
    const inputClasses = classNames(
      "rds-Input",
      {
        [`rds-Input--${size}`]: size !== "md",
        [`rds-Input--length${length?.toUpperCase()}`]: length,
        "is-disabled": disabled,
        "is-invalid": invalid,
      },
      className
    );

    return (
      <div className={inputClasses}>
        {InputIcon && iconPosition === "left" && (
          <InputIcon {...getIconProps(size)} />
        )}
        <input
          ref={ref}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          {...rest}
        />
        {InputIcon && iconPosition === "right" && (
          <InputIcon {...getIconProps(size)} />
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
export default Input;
